// Phase 2.J — reusable matrix for granting/showing detection rule access.
//
// Used by:
//   - CreateLicenseDialog + LicenseDetailDialog (interactive)
//   - portal/account (read-only)
//
// The interactive mode renders chips for every (rule_type, scan_context)
// pair and toggles them on click. Chips are dimmed + tooltip-disabled
// when the chosen product lacks the underlying capability — symmetric
// UX with the existing IoC types matrix above.
import * as React from "react";

import {
    DETECTION_RULE_TYPES,
    productSupportsDetectionScope,
} from "~/lib/licenses";

export interface DetectionScopesMatrixProps {
    /** Current scope grants — {ruleType: [scanContext, ...]}. */
    value: Record<string, string[]>;
    /** Called with the next value when a chip is toggled. */
    onChange?: (next: Record<string, string[]>) => void;
    /**
     * Capability set of the linked product. Drives chip enable/disable.
     * Pass an empty array to leave every chip enabled (admin override).
     */
    productCapabilities?: readonly string[];
    /**
     * Read-only mode: chips render as visual indicators only, no toggling.
     * Used by the customer portal account page.
     */
    readOnly?: boolean;
}

export function DetectionScopesMatrix({
    value,
    onChange,
    productCapabilities,
    readOnly = false,
}: DetectionScopesMatrixProps): React.ReactElement {
    function toggle(ruleType: string, scanContext: string): void {
        if (readOnly || !onChange) return;
        const current = value[ruleType] ?? [];
        const has = current.includes(scanContext);
        const nextForType = has
            ? current.filter((c) => c !== scanContext)
            : [...current, scanContext];
        const next: Record<string, string[]> = { ...value };
        if (nextForType.length === 0) {
            delete next[ruleType];
        } else {
            next[ruleType] = nextForType;
        }
        onChange(next);
    }

    return (
        <div className="stack-sm">
            {DETECTION_RULE_TYPES.map((rt) => {
                const grants = value[rt.slug] ?? [];
                return (
                    <div key={rt.slug} className="stack-xs">
                        <div className="cluster-xs" style={{ alignItems: "center" }}>
                            <span className="chip" style={{ height: 22 }}>
                                {rt.label}
                            </span>
                            {readOnly && grants.length === 0 && (
                                <span className="text-12 text-text-subtle">no access</span>
                            )}
                        </div>
                        <div className="cluster-xs">
                            {rt.contexts.map((ctx) => {
                                const active = grants.includes(ctx.slug);
                                const supported =
                                    !productCapabilities || productCapabilities.length === 0
                                        ? true
                                        : productSupportsDetectionScope(
                                              productCapabilities,
                                              rt.slug,
                                              ctx.slug,
                                          );
                                const disabled = !readOnly && !supported;
                                if (readOnly && !active) {
                                    // In read-only mode, only render granted chips.
                                    return null;
                                }
                                return (
                                    <button
                                        key={ctx.slug}
                                        type="button"
                                        onClick={() => toggle(rt.slug, ctx.slug)}
                                        className={`chip ${active ? "chip--accent" : ""}`}
                                        style={{
                                            cursor: readOnly
                                                ? "default"
                                                : disabled
                                                    ? "not-allowed"
                                                    : "pointer",
                                            height: 24,
                                            opacity: readOnly
                                                ? 1
                                                : disabled
                                                    ? 0.35
                                                    : active
                                                        ? 1
                                                        : 0.6,
                                        }}
                                        aria-pressed={readOnly ? undefined : active}
                                        aria-disabled={disabled}
                                        disabled={disabled || readOnly}
                                        title={
                                            disabled
                                                ? `Product has no capability covering ${rt.label} → ${ctx.label}`
                                                : undefined
                                        }
                                    >
                                        {ctx.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
            {!readOnly && (
                <span className="text-12 text-text-subtle">
                    Empty = no detection access. Disabled chips are not covered by
                    the selected product&rsquo;s capabilities.
                </span>
            )}
        </div>
    );
}
